import React from 'react'

import { Modal, Alert } from '@/components/ui'
import { useApi } from '@/hooks'
import {
  type InsightConfigurationType,
  type InsightDefinition,
  InsightStatus,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import logger from '@/utils/logger'

import { getInsightDefinition, InsightKey } from '../insights'

const log = logger({ category: 'InsightDrilldown' })

const enableFooter = false

export type DrilldownProps = {
  configuration: InsightConfigurationType
  onClose: () => void
}

const InsightWrapper = ({
  configuration,
  onClose,
}: {
  configuration: InsightConfigurationType
  onClose: () => void
}) => {
  const { api } = useApi()
  const _definition = getInsightDefinition(configuration.slug as InsightKey)
  type DataType = Exclude<Awaited<ReturnType<typeof _definition.load>>, InsightStatus>
  type ParamsType = Parameters<typeof _definition.configuration.isValid>[0]
  const definition = _definition as InsightDefinition<ParamsType, DataType>

  const [result, setResult] = React.useState<{ data?: DataType; status: InsightStatus }>({
    data: undefined,
    status: { code: InsightStatusCode.STARTING },
  })

  React.useEffect(() => {
    if (!api) {
      return
    }
    const isValid = definition.configuration.isValid(configuration.parameters as ParamsType)
    if (isValid === true) {
      setResult((previous) => ({ ...previous, status: { code: InsightStatusCode.LOADING } }))
      const fn = async () => {
        try {
          // throw new Error('TEST')
          const response = await definition.load(api, configuration.parameters as ParamsType)
          if (isInsightStatus(response)) {
            setResult({ data: undefined, status: response })
          } else {
            setResult({ data: response as DataType, status: { code: InsightStatusCode.DISPLAYING } })
          }
        } catch (ex) {
          log.error('catch %o', ex)
          setResult({ data: undefined, status: { code: InsightStatusCode.ERROR, message: (ex as Error).message } })
        }
      }
      fn()
    } else {
      setResult({
        data: undefined,
        status: {
          code: InsightStatusCode.NOT_CONFIGURED,
          message: isValid === false ? '' : isValid,
        },
      })
    }
  }, [api, configuration.parameters])

  const error = (result.status.code === InsightStatusCode.ERROR && result.status.message) || ''
  const errorAlert = error ? <Alert type="error" message={error} /> : null

  const content =
    definition && !error ? (
      <definition.Display configuration={configuration} status={result.status} data={result.data} />
    ) : null

  const footer =
    enableFooter && definition?.Footer ? (
      <definition.Footer configuration={configuration} status={result.status} data={result.data} />
    ) : null

  return (
    <Modal
      maxWidth="70vw"
      title={configuration.title}
      show
      onHide={onClose}
      // hideFooter={true}
      bodyContent={
        <div className="flex flex-1">
          <div className="min-h-[40vh] max-h-[70vh] w-full bg-white overflow-auto">
            {errorAlert ? errorAlert : content}
          </div>
          {footer && (
            <footer className="px-4 py-2 justify-between w-full items-center shrink-0 border-t border-base-200 flex">
              <div className="w-full text-sm ">{footer}</div>
            </footer>
          )}
        </div>
      }
    />
  )
}

const Drilldown = ({ configuration, onClose }: DrilldownProps) => {
  return <InsightWrapper configuration={{ ...configuration, isDrilldown: true }} onClose={onClose} />
}

export default Drilldown
